/**
 * Список чатов.
 *
 * Раскладка строки повторяет Telegram: круглый аватар слева, название и время в
 * первой строке, служебная информация и счётчик непрочитанного во второй.
 * Менеджер сканирует такой список по аватарам и не читает названия подряд.
 *
 * В режиме «Все аккаунты» каждая строка несёт метку аккаунта — прямое
 * требование §5 ТЗ. Без неё менеджер, работающий с четырьмя аккаунтами, не
 * понимает, на какой из них пришло сообщение, и отвечает не от того лица.
 *
 * Вторая строка занята статусом, ответственным и тегами, а не превью
 * последнего сообщения: в TelegramChatDto текста последнего сообщения нет.
 * Подставлять туда заглушку нельзя — пустая строка читается как «сообщений
 * нет», хотя переписка есть.
 */

import { useEffect, useRef } from "react";
import type { TelegramChatDto } from "@tw/shared-types";
import type { ChatActivity } from "@/lib/chat-actions.ts";
import { TypingDots } from "./TypingDots.tsx";
import { accountLabel } from "./accountLabel.js";
import { Avatar } from "./Avatar.tsx";
import { AlertIcon, CheckIcon, ChecksIcon, ClockIcon, PinIcon } from "./icons.tsx";
import { chatTypeBadge } from "./chatType.js";
import { chatTitle } from "./chatTitle.js";
import { cn, formatCount } from "@/lib/utils";
import { EmptySearchArt } from "./EmptyArt.tsx";
import { seedColor } from "../lib/seedColor.js";

interface ChatListProps {
  chats: TelegramChatDto[];
  /** Кто печатает — по id чата. Показывается вместо превью, как в Telegram. */
  activity?: Record<string, ChatActivity>;
  activeChatId: string | null;
  loading: boolean;
  showAccountBadge: boolean;
  onSelect: (chat: TelegramChatDto) => void;
  /** Есть ли ещё страница ниже. Старая версия WorkspaceView не передаёт. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /**
   * Подгрузка следующей страницы упала. Пока так — часовой не следит за
   * прокруткой, а повтор только по кнопке: иначе упавший запрос тут же
   * перезапускался бы снова и снова, без паузы.
   */
  loadMoreFailed?: boolean;
  /** Идентификаторы отмеченных чатов. Пустое множество = режим выбора выключен. */
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (chatId: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  NEW: "Новый",
  IN_PROGRESS: "В работе",
  WAITING: "Ожидание",
  DONE: "Завершён",
  ARCHIVED: "Архив",
};

/*
 * Цвет точки статуса.
 *
 * Статус показывается точкой и подписью, а не цветной плашкой. Причина не
 * вкусовая: плашка красит фон, и на строке, где уже есть метка аккаунта, метка
 * типа и теги, четыре разноцветных прямоугольника читаются как мусор. Точка
 * занимает 6 px, не спорит с соседями и сканируется быстрее — на этом держится
 * весь колоночный интерфейс, из которого приём взят.
 *
 * Цвета — заливки, а не цвет текста: ни один из них не проходит контраст как
 * текст, и подпись рядом идёт приглушённым нейтральным.
 */
const STATUS_DOT: Record<string, string> = {
  NEW: "text-dot-new",
  IN_PROGRESS: "text-brand",
  WAITING: "text-dot-progress",
  DONE: "text-dot-done",
  ARCHIVED: "text-muted-foreground",
};

export function ChatList({
  chats,
  activity,
  activeChatId,
  loading,
  showAccountBadge,
  onSelect,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  loadMoreFailed = false,
  selectedIds,
  onToggleSelect,
}: ChatListProps) {
  const selecting = selectedIds.size > 0;

  /*
   * Докачка по прокрутке.
   *
   * Часовой — пустой элемент в конце списка; как только он попадает в
   * область видимости, просим следующую страницу. Кнопка «ещё» здесь хуже:
   * менеджер ищет чат глазами, прокручивая, и остановка на кнопке ломает
   * этот жест.
   */
  const sentinel = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || loadingMore || loadMoreFailed || !onLoadMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMoreFailed, onLoadMore, chats.length]);
  if (loading) {
    /*
     * Скелет, а не слово «Загрузка».
     *
     * Текст на месте будущего списка меняет высоту содержимого дважды: сперва
     * появляется, потом исчезает вместе с приходом строк. Скелет той же
     * геометрии, что и строка, оставляет раскладку на месте.
     */
    return (
      <ul
        className="min-h-0 flex-1 space-y-0.5 overflow-hidden p-1.5"
        aria-busy
      >
        {Array.from({ length: 7 }, (_, i) => (
          <li key={i} className="flex items-center gap-2.5 rounded-md px-2 py-1.5">
            <span className="size-12 flex-none animate-pulse rounded-full bg-secondary" />
            <span className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span
                className="h-3 animate-pulse rounded-full bg-secondary"
                style={{ width: `${55 + ((i * 13) % 30)}%` }}
              />
              <span
                className="h-2.5 animate-pulse rounded-full bg-secondary/70"
                style={{ width: `${35 + ((i * 17) % 40)}%` }}
              />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-6 py-10 text-center">
        <EmptySearchArt className="size-24" />
        <p className="text-sm font-medium text-heading">Чатов нет</p>
        {/* Причина названа прямо: пустой список у менеджера почти всегда
            означает отсутствие назначений, а не отсутствие переписки. */}
        <p className="text-xs leading-relaxed text-muted-foreground">
          Либо под текущие фильтры ничего не подошло, либо на ваш аккаунт ещё не
          выдан доступ.
        </p>
      </div>
    );
  }

  return (
    <ul className="min-h-0 flex-1 space-y-px overflow-y-auto p-1.5">
      {chats.map((chat) => {
        const title = chatTitle(chat.title);
        const isActive = chat.id === activeChatId;
        const checked = selectedIds.has(chat.id);

        return (
          <li key={chat.id}>
            <button
              type="button"
              /*
               * Пока хоть один чат отмечен, нажатие по строке переключает
               * отметку, а не открывает чат. Иначе набор выделения
               * оборвался бы на первом же случайном клике.
               */
              onClick={() =>
                selecting ? onToggleSelect(chat.id) : onSelect(chat)
              }
              className={cn(
                "group/row flex w-full items-center gap-2.5 rounded-lg border-transparent bg-transparent px-2 py-1.5 text-left transition-colors",
                checked && !isActive && "bg-secondary",
                /*
                 * Заливка одним тоном вместо градиента, и никакого подъёма по
                 * наведению. Движение в системе сведено к смене цвета и тени:
                 * список из полусотни строк, каждая из которых подпрыгивает
                 * под курсором, читается дёргано.
                 */
                isActive
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "hover:bg-secondary",
              )}
            >
              {/* Флажок появляется по наведению и остаётся у отмеченных:
                  постоянный столбец флажков ради редкой операции съедал бы
                  ширину узкой колонки. */}
              <span
                role="checkbox"
                aria-checked={checked}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect(chat.id);
                }}
                className={cn(
                  "grid size-4 flex-none place-items-center rounded-[5px] border transition-opacity",
                  checked
                    ? "border-transparent bg-primary text-primary-foreground opacity-100"
                    : "border-border opacity-0 group-hover/row:opacity-100",
                  selecting && "opacity-100",
                )}
              >
                {checked && <CheckIcon className="size-2.5" />}
              </span>

              <Avatar
                name={title}
                seed={chat.id}
                size="chat"
                photoUrl={chat.hasPhoto ? `/api/chats/${chat.id}/photo` : null}
              />

              <span className="flex min-w-0 flex-1 flex-col">
                {/*
                  Раскладка строки повторяет Telegram: первая строка —
                  название и время, вторая — превью и счётчик непрочитанного.
                  Наши служебные поля (статус, ответственный, теги) уходят
                  третьей строкой и появляются только когда есть что показать:
                  в Telegram третьей строки нет, и занимать её всегда значит
                  ломать привычный ритм списка.
                */}
                {/*
                  На первой строке ТОЛЬКО название и время.
                  Метка аккаунта и тип чата стояли здесь же, и на живых данных
                  стало видно, во что это обходится: «Escorts Royal» ужималось
                  до «Esco…», «Sweden family» до «Swed…». Два служебных значка
                  съедали строку, а обрезалось при этом единственное, ради чего
                  список читают.

                  Оба значка переехали на вторую строку — они по-прежнему
                  видны всегда (метка аккаунта обязательна по §5 ТЗ), но
                  теперь платят за себя превью, а не названием.
                */}
                <span className="flex items-baseline gap-1.5">
                  <span className="min-w-0 flex-1 truncate text-[14px] font-medium">
                    {title}
                  </span>
                  {/* Закреплён в Telegram: строка стоит сверху, и значок
                      объясняет почему — иначе порядок кажется сломанным. */}
                  {chat.isPinned && (
                    <PinIcon
                      aria-label="Закреплён"
                      className={cn(
                        "size-3 flex-none self-center",
                        isActive ? "text-primary-foreground/80" : "text-muted-foreground",
                      )}
                    />
                  )}
                  <span
                    className={cn(
                      "ml-auto flex flex-none items-center gap-0.5 text-[11px]",
                      isActive
                        ? "text-primary-foreground/90"
                        : "text-muted-foreground",
                    )}
                  >
                    <LastMessageTicks status={chat.lastMessageStatus} active={isActive} />
                    {formatTime(chat.lastMessageAt)}
                  </span>
                </span>

                {/*
                  Вторая строка — превью последнего сообщения, как в любом
                  мессенджере. Раньше её занимали служебные поля, потому что
                  текста последнего сообщения в DTO просто не было: в Postgres
                  лежит лишь то, что прошло через систему, то есть единицы
                  записей на сотни чатов. Теперь превью приходит из TDLib
                  вместе со снимком чата.
                */}
                <span className="flex items-center gap-1.5">
                  {/*
                    Метка аккаунта окрашена ПО АККАУНТУ, а не единым брендовым
                    синим.

                    Общий ящик собирает чаты с нескольких номеров, и вопрос «от
                    кого пойдёт ответ» здесь важнее почти всех остальных.
                    Одинаково синие метки на всех строках отвечали на него
                    только чтением текста; цвет отвечает до чтения.

                    Тон берётся из того же источника, что и аватар, поэтому
                    аккаунт узнаётся одинаково в обоих местах.
                  */}
                  {showAccountBadge && (
                    <span
                      style={
                        isActive
                          ? undefined
                          : { color: seedColor(chat.account.id) }
                      }
                      className={cn(
                        "flex-none text-[11px] font-semibold",
                        isActive && "text-primary-foreground/90",
                      )}
                    >
                      [{accountLabel(chat.account)}]
                    </span>
                  )}
                  {/* Метка типа: канал и группа выглядели одинаково, и понять,
                      где ждать ответа, было нельзя. */}
                  {chatTypeBadge(chat.type) && (
                    <span
                      className={cn(
                        "flex-none rounded-xs px-1 py-px text-[10px] font-medium uppercase tracking-wide",
                        isActive
                          ? "bg-black/15 text-primary-foreground"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {chatTypeBadge(chat.type)}
                    </span>
                  )}
                  {activity?.[chat.id] ? (
                    <span
                      className={cn(
                        "min-w-0 flex-1 truncate text-[13px] font-medium",
                        isActive ? "text-primary-foreground" : "text-brand",
                      )}
                    >
                      {activity[chat.id]!.label}
                      <TypingDots />
                    </span>
                  ) : (
                    <span
                      className={cn(
                        "min-w-0 flex-1 truncate text-[13px]",
                        isActive
                          ? "text-primary-foreground/90"
                          : "text-muted-foreground",
                      )}
                    >
                      {chat.lastMessagePreview ?? "\u00a0"}
                    </span>
                  )}

                  {chat.unreadCount > 0 && (
                    <span
                      className={cn(
                        "grid h-[22px] min-w-[22px] flex-none place-items-center rounded-full px-1.5 text-[12px] font-medium",
                        isActive
                          ? "bg-primary-foreground text-primary"
                          : "bg-primary text-primary-foreground",
                      )}
                    >
                      {formatCount(chat.unreadCount)}
                    </span>
                  )}
                </span>

                <span className="flex min-w-0 items-center gap-1.5 text-xs">
                  {chat.internalStatus !== "NEW" && (
                    <span
                      className={cn(
                        "flex flex-none items-center gap-1.5 text-[11px]",
                        isActive
                          ? "text-primary-foreground/90"
                          : "text-muted-foreground",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "status-dot",
                          isActive
                            ? "text-primary-foreground"
                            : (STATUS_DOT[chat.internalStatus] ??
                                "text-muted-foreground"),
                        )}
                      />
                      {STATUS_LABELS[chat.internalStatus] ??
                        chat.internalStatus}
                    </span>
                  )}

                  {chat.assignedManager && (
                    <span
                      className={cn(
                        "flex-none text-[11px]",
                        isActive
                          ? "text-primary-foreground/90"
                          : "text-muted-foreground",
                      )}
                    >
                      {chat.assignedManager.displayName}
                    </span>
                  )}

                  {(chat.tags ?? []).slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className={cn(
                        "flex-none rounded-full px-2 py-px text-[11px]",
                        isActive
                          ? "bg-black/15 text-primary-foreground"
                          : "bg-secondary text-muted-foreground",
                      )}
                    >
                      {tag}
                    </span>
                  ))}

                  {/*
                    Username показывается, только когда второй строки нет
                    вовсе: у чата без переписки. Иначе он дублирует то, что
                    и так видно в карточке, и отнимает место у превью.
                  */}
                  {chat.peerUsername &&
                    !chat.lastMessagePreview &&
                    !chat.assignedManager &&
                    (chat.tags ?? []).length === 0 && (
                      <span
                        className={cn(
                          "truncate",
                          isActive
                            ? "text-primary-foreground/90"
                            : "text-muted-foreground",
                        )}
                      >
                        @{chat.peerUsername}
                      </span>
                    )}
                </span>
              </span>
            </button>
          </li>
        );
      })}
      {hasMore && (
        <li ref={sentinel} aria-hidden={!loadMoreFailed} className="flex justify-center py-3">
          {loadingMore && (
            <span className="h-2.5 w-24 animate-pulse rounded-full bg-secondary" />
          )}
          {loadMoreFailed && !loadingMore && (
            <button
              type="button"
              onClick={onLoadMore}
              className="text-xs text-muted-foreground hover:text-brand"
            >
              Не удалось загрузить — повторить
            </button>
          )}
        </li>
      )}
    </ul>
  );
}

/**
 * Относительное время: «14:32» для сегодняшних, дата для остальных.
 * В списке важнее «сегодня или нет», чем точная дата.
 */
function formatTime(iso: string | null): string {
  if (!iso) return "";

  const date = new Date(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  return sameDay
    ? date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}

/**
 * Галочки последнего сообщения перед временем, как в Telegram: по ним видно,
 * прочитал ли клиент ответ, не открывая чат. У входящего и у неизвестного
 * статуса (чат синхронизирован до появления поля) — ничего: лучше пусто,
 * чем угаданная галочка. Вид — тот же, что у пузыря в MessageList.
 */
function LastMessageTicks({
  status,
  active,
}: {
  status: TelegramChatDto["lastMessageStatus"];
  active: boolean;
}) {
  const tone = active ? "" : "text-brand";
  switch (status) {
    case "read":
      return (
        <span title="Прочитано" className={cn("inline-flex", tone)}>
          <ChecksIcon className="size-[15px]" />
        </span>
      );
    case "sent":
      return (
        <span title="Отправлено" className={cn("inline-flex", tone)}>
          <CheckIcon className="size-[15px]" />
        </span>
      );
    case "pending":
      return (
        <span title="Отправляется" className="inline-flex">
          <ClockIcon className="size-[13px]" />
        </span>
      );
    case "failed":
      return (
        <span title="Не отправлено" className="inline-flex text-destructive">
          <AlertIcon className="size-[13px]" />
        </span>
      );
    default:
      return null;
  }
}
